import { z } from "zod";
import { AssignSectorInput, CreateSectorInput, UpdateSectorInput } from "./api";
import {
  FindingCategory,
  ParticipantRole,
  ParticipantStatus,
  SectorStatus,
  UnitType,
} from "./enums";
import { FindingIcon } from "./finding-icons";
import { MultiPolygonGeometry, PointGeometry } from "./geo";

// HQ mode of the responder app (issue #182): a participant with the role "hq" gets the HQ tab,
// where they see everyone and run sectors, assignments and groups without a computer. The
// requests go to /api/field/hq/* with the phone's session token; they need the network (no
// outbox), so the tab says so when the phone is offline.

const Id = z.uuid();
const Timestamp = z.iso.datetime({ offset: true });

export const HqGroup = z.object({ id: Id, name: z.string(), color: z.string().nullable() });
export type HqGroup = z.infer<typeof HqGroup>;

export const HqPerson = z.object({
  id: Id,
  name: z.string(),
  groupId: Id.nullable(),
  role: ParticipantRole,
  status: ParticipantStatus,
  /** A drone ("uav") gets its own marker on the HQ map (#355). */
  unitType: UnitType,
  /** False while waiting for HQ approval. */
  approved: z.boolean(),
  /** Last position, null before the first fix or when older than the share view's limit. */
  location: PointGeometry.nullable(),
  at: Timestamp.nullable(),
  batteryPct: z.number().int().nullable(),
});
export type HqPerson = z.infer<typeof HqPerson>;

export const HqSector = z.object({
  id: Id,
  name: z.string(),
  status: SectorStatus,
  priority: z.number().int(),
  areaHa: z.number(),
  /** Coverage by track (estimated), percent. */
  coveredPct: z.number(),
  geometry: MultiPolygonGeometry,
  /** The group searching it now (latest open assignment). */
  groupId: Id.nullable(),
});
export type HqSector = z.infer<typeof HqSector>;

export const HqFinding = z.object({
  id: Id,
  category: FindingCategory,
  icon: FindingIcon.nullable(),
  description: z.string().nullable(),
  location: PointGeometry,
  foundAt: Timestamp,
  /** Who reported it: a responder's name or the HQ member's. */
  reporterName: z.string(),
  /** A stored photo: GET /api/field/hq/photos/:id with the session token. */
  photoId: Id.nullable(),
});
export type HqFinding = z.infer<typeof HqFinding>;

export const HqOverview = z.object({
  groups: z.array(HqGroup),
  people: z.array(HqPerson),
  sectors: z.array(HqSector),
  findings: z.array(HqFinding),
});
export type HqOverview = z.infer<typeof HqOverview>;

export const HqMoveInput = z.object({ groupId: Id.nullable() });
export type HqMoveInput = z.infer<typeof HqMoveInput>;

/**
 * What the phone creates carries its own id, kept while the same change is retried: a retry after
 * a timeout that actually reached the server returns the first result instead of a duplicate
 * (review of #182, issue #187). Optional, because installed apps without it must keep working.
 */
const ClientId = { id: Id.optional() };

export const HqCreateSectorInput = CreateSectorInput.extend(ClientId);
export type HqCreateSectorInput = z.infer<typeof HqCreateSectorInput>;

export const HqAssignInput = AssignSectorInput.extend(ClientId);
export type HqAssignInput = z.infer<typeof HqAssignInput>;

export const HqCreateGroupInput = z.object({ name: z.string().trim().min(1).max(60), ...ClientId });
export type HqCreateGroupInput = z.infer<typeof HqCreateGroupInput>;

/** Roles HQ in the app may hand out: not "hq" nor "commander", those stay with the panel. */
export const HQ_INVITE_ROLES = ["searcher", "leader"] as const;

export const HqCreateInviteInput = z.object({
  groupId: Id.nullable(),
  role: z.enum(HQ_INVITE_ROLES).default("searcher"),
});
export type HqCreateInviteInput = z.infer<typeof HqCreateInviteInput>;

/** The link for the QR code; the token inside is shown once and never stored in plain. */
export const HqInvite = z.object({ link: z.string(), expiresAt: Timestamp });
export type HqInvite = z.infer<typeof HqInvite>;

/** HQ changes a sector's name, status or priority from the phone (#190); not its outline. */
export const HqUpdateSectorInput = UpdateSectorInput.pick({
  name: true,
  status: true,
  priority: true,
});
export type HqUpdateSectorInput = z.infer<typeof HqUpdateSectorInput>;
